// @flow
import * as React from 'react'
import ReactDOM from 'react-dom'
import { Transition } from 'react-transition-group'

// Types
export type Props = {
  showed: boolean,
  duration: number,
  render: boolean => any,
}

// Internalts
let Z_INDEX_COUNTER = 10000
const IS_REACT_16 = ReactDOM.createPortal !== undefined
const IS_SERVER = typeof document === 'undefined'

// Utils
const createPortal: Function = IS_REACT_16
  ? ReactDOM.createPortal
  : ReactDOM.unstable_renderSubtreeIntoContainer

function renderLightbox(props: Props) {
  return (
    <Transition
      in={props.showed}
      timeout={{ enter: 1, exit: props.duration }}
      mountOnEnter
      unmountOnExit
      appear
    >
      {state => props.render(state === 'entered')}
    </Transition>
  )
}

/**
 * Component that shows what returned by `render` prop in a portal.
 * The `render` function called with one boolean argument `showed`,
 * which could be used to animate internal content in 650ms (with CSS).
 */
class AnimatedPortal extends React.Component<Props> {
  unmounted = false

  node = null

  hideTimer = null

  componentDidMount() {
    if (IS_SERVER) return

    this.node = !IS_REACT_16 ? document.createElement('div') : this.node

    if (this.node) {
      this.node.style.position = 'relative'
      if (document.body) {
        document.body.appendChild(this.node)
      }
      if (this.props.showed) {
        this.animateIn(this.props)
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.showed && nextProps.showed !== this.props.showed) {
      this.animateIn(nextProps)
    } else if (!IS_REACT_16) {
      this.renderPort(nextProps)
    }
  }

  componentWillUnmount() {
    if (this.node) {
      this.unmounted = true
      this.animateOut(this.props)
    }
  }

  animateIn = (props: Props) => {
    if (this.node) {
      this.node.style.zIndex = `${Z_INDEX_COUNTER++}`
    }
    !IS_REACT_16 && this.renderPort(props)
  }

  animateOut = (props: Props) => {
    !IS_REACT_16 && this.renderPort(props)
    this.hideTimer = setTimeout(this.maybeUnmountComponent, props.duration)
  }

  maybeUnmountComponent = () => {
    if (IS_SERVER) return

    if (this.unmounted && this.node) {
      !IS_REACT_16 && ReactDOM.unmountComponentAtNode(this.node)
      if (document.body) {
        document.body.removeChild(this.node)
        this.node = null
      }
    }
  }

  renderPort = (props: Props, callback?: Function) => {
    createPortal(this, renderLightbox(props), this.node, callback)
  }

  render() {
    if (!IS_REACT_16 || IS_SERVER) {
      return null
    }
    if (!this.node && IS_REACT_16) {
      this.node = document.createElement('div')
    }
    return createPortal(renderLightbox(this.props), this.node)
  }
}

export default AnimatedPortal
